/* Which modules a salon is offered, and which one it lands on.
 *
 * Split out of js/apps.js for the reason js/rules/queue.js and licence.js were
 * split out of theirs: apps.js reaches licensing.js, which reaches sync.js,
 * which throws without a Supabase config — so nothing in that file could be
 * tested at all. What is left there is the registry, the localStorage key and
 * the Vue reactivity. What is here is every decision.
 *
 * `licensed` is passed in as a function rather than imported, which is what
 * keeps this file pure. In the app it is `licensing.licensed`; in the tests it
 * is a set. It answers about a FEATURE id, which defaults to the module id.
 *
 * ── THE ONE THAT MUST NOT BE WRONG ──────────────────────────────────────────
 *
 * `resolveActive`. The switcher is hidden when a salon has one module, so it is
 * no longer always true that there is something on screen to navigate with. A
 * remembered module the salon has since lost has to fall through to one they
 * still have, or the console is a stub explaining what they cannot use with no
 * way off it — and no menu to leave by, because hiding the menu is exactly what
 * put them there. That is the case the tests below exist for.
 */

function featureOf(app) {
  return app.feature || app.id;
}

/* Is this module on this salon's plan, and switched on there? `available: false` is a
 * module licensed and off for now: not listed, and nowhere to land.
 *
 * Deliberately not asked via stateOf: that answers 'planned' before it ever
 * looks at the licence, which is right for deciding what to draw and wrong for
 * deciding whether to draw it. A module that is both unbuilt and unsold should
 * not be advertised on the strength of being unbuilt.
 */
export function offers(app, licensed) {
  if (!app || app.available === false) return false;
  return !!licensed(featureOf(app));
}

/* What a module IS, once it is offered.
 *
 *   'planned'  registered as a placeholder; nothing built behind it yet
 *   'locked'   built, and not on this plan
 *   'ready'    built, and usable
 *
 * Planned beats locked: a module that does not exist yet is not something a
 * salon can be sold, so telling them it is not on their plan would be a lie
 * about a placeholder.
 */
export function stateOf(app, licensed) {
  if (!app) return 'ready';
  if (app.status === 'planned') return 'planned';
  return licensed(featureOf(app)) ? 'ready' : 'locked';
}

// Everything this salon is offered, in registry order.
export function offeredList(registry, licensed) {
  return registry.filter(function (a) { return offers(a, licensed); });
}

/* Is there anything to switch BETWEEN? One module is not a choice, and a menu
 * offering it opens to show you the screen you are already on. */
export function switchable(registry, licensed) {
  return offeredList(registry, licensed).length > 1;
}

// Built and usable — what somebody can actually be dropped onto.
export function readyList(registry, licensed) {
  return registry.filter(function (a) { return a.available !== false && stateOf(a, licensed) === 'ready'; });
}

/* Which module the console shows.
 *
 * The remembered choice wins, but only while the salon is still offered it. A
 * remembered id that no longer exists at all — renamed, removed — falls through
 * the same way, which is what this did before licensing existed.
 *
 * The order of the fallbacks is the point. The Turn Tracker first, when it is on
 * the plan: it is what the desk is here for, and alphabetical order would put an
 * owner's first login on DreamCloud. Then something else built and usable; then
 * anything they are offered, which covers a plan of nothing but placeholders and
 * lands on a stub that at least says "coming"; then null, for a salon offered
 * nothing whatsoever. No module is named to them in that case, because none of
 * them is theirs: the shell says there are none and to contact support.
 */
var DEFAULT_ID = 'turn-tracker';

export function resolveActive(registry, rememberedId, licensed) {
  var remembered = null;
  for (var i = 0; i < registry.length; i++) {
    if (registry[i].id === rememberedId) { remembered = registry[i]; break; }
  }
  if (remembered && offers(remembered, licensed)) return remembered;

  var ready = readyList(registry, licensed);
  for (var j = 0; j < ready.length; j++) {
    if (ready[j].id === DEFAULT_ID) return ready[j];
  }
  // `landing: false` is a module only ever chosen. The registry is alphabetical, so
  // otherwise a desk that has never touched the switcher would come in on the Optimizer
  // instead of the board. It is still somewhere to land if there is nothing else.
  var landing = ready.filter(function (a) { return a.landing !== false; });
  if (landing.length) return landing[0];
  if (ready.length) return ready[0];

  var offered = offeredList(registry, licensed);
  return offered[0] || null;
}

/* The count a module shows on the switcher, or null for none. A module with no `badge`
 * renders as it always did, and neither does zero say anything: it is there to be quiet. */
export function badgeOf(app) {
  var n = app ? app.badge : null;
  return typeof n === 'number' && n > 0 ? n : null;
}
